import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { flexRender } from "@tanstack/react-table";
import { RefreshIcon } from "@8x8/oxygen-icon/icons";
import {
  StyledPlusCircleIcon,
  StyledRedTrashIcon,
} from "../styled/StyledIcons";
import { StyledTableHeadRow } from "../styled/table/StyledTableRow";
import Button from "@8x8/oxygen-button";
import ColumnSelector from "@/react/components/table/components/ColumnSelector";
import { KEY, newRow } from "@/react/components/table/useTableMutations";
import { Spinner } from "@8x8/oxygen-loaders";
import TableHeadCell from "@/react/components/table/components/TableHeadCell";
import TablePagination from "@/react/components/table/components/TablePagination";
import TableEditableRow from "@/react/components/table/components/TableEditableRow";
import { ScrollSync, ScrollSyncPane } from "react-scroll-sync";
import { StyledTableFlex } from "@/react/components/table/components/StyledTableFlex";
import { DisplayFilters } from "@/react/components/table/components/filter/DisplayFilters";
import { ObjectSchema } from "yup";
import FileActions, {
  fileActionsProps,
} from "@/react/components/table/components/file-actions/FileActions";
import TableMutations from "@/react/components/table/components/TableMutations";
import {
  ColumnSelectorWrapper,
  FlexButton,
  FooterButton,
  FooterButtonsContainer,
  PaginationContainer,
  TableActionsSection,
  TableActionsWrapper,
  TableContainer,
  TableFooterContainer,
  TableHeadContainer,
  TableLoadingSpinnerWrapper,
  TableRowsContainer,
} from "./styled";
import TablePartialDataIndicator from "@/react/components/table/components/TablePartialDataIndicator";

function TableComponent(props) {
  const {
    displayFilters,
    setFilterValue,
    setFilterFnId,
    addDisplayFilter,
    removeFilter,
  } = props.filters;
  const {
    addRows,
    deleteRows,
    updateRows,
    bulkUpdateRows,
    hasErrors,
    rowsToUpdate,
    rowsToCreate,
    rowsToDelete,
  } = props.mutations;

  const { columnVisibility, setColumnVisibility, setRowSelection } =
    props.state;

  const onAddRow = useCallback(() => {
    addRows([newRow(props.table.getAllColumns())], props.schema);
  }, [addRows, props.table.getAllColumns(), props.schema]);

  const onDeleteRows = useCallback(() => {
    deleteRows(
      props.table.getSelectedRowModel().rows.map((row) => row.original[KEY])
    );
    props.table.toggleAllRowsSelected(false);
  }, [props.table.getSelectedRowModel().rows]);

  const tableActions = useMemo(
    () => (
      <>
        {props.actionsSlot && props.actionsSlot(props.table)}
        {addRows && (
          <FlexButton variant="tertiary" onClick={onAddRow}>
            <StyledPlusCircleIcon />
            <span>Add</span>
          </FlexButton>
        )}
        {deleteRows && (
          <FlexButton
            variant="tertiary"
            isDisabled={!props.table.getSelectedRowModel().rows.length}
            onClick={onDeleteRows}
          >
            <StyledRedTrashIcon />
            <span>Delete</span>
          </FlexButton>
        )}
      </>
    ),
    [
      props.actionsSlot,
      onAddRow,
      onDeleteRows,
      props.table.getSelectedRowModel().rows.length,
    ]
  );

  const onBeforeCancel = useCallback(() => {
    setRowSelection({});
    props.onCancel();
  }, [props.onCancel]);

  const onBeforeRefresh = useCallback(() => {
    setRowSelection({});
    props.onRefresh && props.onRefresh();
  }, [props.onRefresh]);

  const rowsToSet =
    props.table.getSelectedRowModel().flatRows.length > 0
      ? props.table.getSelectedRowModel().flatRows
      : props.table.getPrePaginationRowModel().flatRows;

  return (
    <TableContainer>
      {(props.isLoading || props.mutations.isProcessing) && (
        <TableLoadingSpinnerWrapper data-test-id={"table-loading-spinner"}>
          <Spinner />
        </TableLoadingSpinnerWrapper>
      )}

      <TableActionsSection>
        {props.enableHiding && (
          <ColumnSelectorWrapper>
            {props.renderColumnSelector &&
              props.renderColumnSelector(
                props.table.getAllColumns(),
                columnVisibility,
                setColumnVisibility
              )}
          </ColumnSelectorWrapper>
        )}
        <TableActionsWrapper>{tableActions}</TableActionsWrapper>
        {props.fileActions && (
          <FileActions
            title={props.title}
            addRows={addRows}
            bulkUpdateRows={bulkUpdateRows}
            allColumns={props.table.getAllColumns()}
            selectedRowModel={props.table.getSelectedRowModel()}
            prePaginationRowModel={props.table.getPrePaginationRowModel()}
            schema={props.schema}
            fileActions={props.fileActions}
            processFileUpload={props?.processFileUpload}
          />
        )}
      </TableActionsSection>

      {displayFilters?.size > 0 && (
        <DisplayFilters
          table={props.table}
          displayFilters={displayFilters}
          setFilterValue={setFilterValue}
          setFilterFnId={setFilterFnId}
          removeFilter={removeFilter}
        />
      )}
      <ScrollSync>
        <StyledTableFlex
          columns={props.table.getVisibleFlatColumns()}
          data-table-title={props.title}
        >
          <ScrollSyncPane>
            <TableHeadContainer key="header">
              {props.table.getHeaderGroups().map((headerGroup) => (
                <StyledTableHeadRow key={headerGroup.id} role="row">
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHeadCell
                        key={header.id}
                        headerRender={flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        tooltip={header.column.columnDef.tooltip}
                        canResize={header.column.getCanResize()}
                        canEdit={
                          header.column.columnDef.editable === true &&
                          updateRows
                        }
                        canFilter={header.column.getCanFilter()}
                        addBlankFilter={() => {
                          addDisplayFilter(header.column);
                        }}
                        canSort={header.column.getCanSort()}
                        size={header.column.getSize()}
                        resizeHandler={header.getResizeHandler()}
                        isResizing={header.column.getIsResizing()}
                        column={header.column}
                        rows={rowsToSet}
                        table={props.table}
                      />
                    );
                  })}
                </StyledTableHeadRow>
              ))}
            </TableHeadContainer>
          </ScrollSyncPane>
          <ScrollSyncPane>
            <TableRowsContainer>
              {props.table.getRowModel().rows.map((row) => (
                <React.Fragment key={row.id}>
                  <TableEditableRow
                    rowsToUpdate={rowsToUpdate}
                    rowsToCreate={rowsToCreate}
                    row={row}
                    onRowClick={props.onRowClick}
                  />
                  {row.getIsExpanded() && props.renderSubComponent({ row })}
                </React.Fragment>
              ))}
            </TableRowsContainer>
          </ScrollSyncPane>
        </StyledTableFlex>
      </ScrollSync>
      <TableFooterContainer>
        <FooterButtonsContainer>
          {(addRows || deleteRows || updateRows) && (
            <>
              <FooterButton
                onClick={props.onSave}
                isDisabled={hasErrors || !props.isDirty || props.isLoading}
              >
                Save
              </FooterButton>
              <FooterButton
                onClick={onBeforeCancel}
                isDisabled={!props.isDirty || props.isLoading}
                variant="tertiary"
              >
                Cancel
              </FooterButton>
            </>
          )}

          {props.onRefresh && (
            <Button
              onClick={onBeforeRefresh}
              isDisabled={props.isLoading}
              variant="tertiary"
              size="small"
            >
              <RefreshIcon size={18} />
            </Button>
          )}
        </FooterButtonsContainer>
        {props.table.initialState.pagination?.pageSizes?.length > 0 && (
          <PaginationContainer>
            <TableMutations
              table={props.table}
              rowsToCreate={rowsToCreate}
              rowsToUpdate={rowsToUpdate}
              rowsToDelete={rowsToDelete}
            />
            {props.isPartialData && (
              <TablePartialDataIndicator onAbort={props.onAbort} />
            )}
            <TablePagination
              hasNextPage={props.table.getCanNextPage()}
              hasPreviousPage={props.table.getCanPreviousPage()}
              nextPage={props.table.nextPage}
              pageCount={props.table.getPageCount()}
              pageIndex={props.table.getState().pagination.pageIndex}
              pageSize={props.table.getState().pagination.pageSize}
              previousPage={props.table.previousPage}
              setPageIndex={props.table.setPageIndex}
              setPageSize={props.table.setPageSize}
              totalRows={props.table.getPrePaginationRowModel().rows.length}
              pageSizes={props.table.initialState.pagination?.pageSizes}
            />
          </PaginationContainer>
        )}
      </TableFooterContainer>
    </TableContainer>
  );
}

TableComponent.propTypes = {
  title: PropTypes.string.isRequired,
  schema: PropTypes.instanceOf(ObjectSchema).isRequired,
  isDirty: PropTypes.bool,
  isLoading: PropTypes.bool,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  onRefresh: PropTypes.func,
  onAbort: PropTypes.func,
  actionsSlot: PropTypes.func,
  // Table instance from useTable hook
  table: PropTypes.object.isRequired,
  filters: PropTypes.shape({
    displayFilters: PropTypes.instanceOf(Map),
    addDisplayFilter: PropTypes.func,
    setFilterValue: PropTypes.func,
    setFilterFnId: PropTypes.func,
    removeFilter: PropTypes.func,
  }),
  mutations: PropTypes.shape({
    addRows: PropTypes.func,
    deleteRows: PropTypes.func,
    updateRows: PropTypes.func,
    bulkUpdateRows: PropTypes.func,
    hasErrors: PropTypes.bool,
    isProcessing: PropTypes.bool,
    rowsToUpdate: PropTypes.instanceOf(Map),
    rowsToCreate: PropTypes.instanceOf(Map),
    rowsToDelete: PropTypes.instanceOf(Map),
  }),
  fileActions: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape(fileActionsProps),
  ]),
  isFileActions: PropTypes.bool,
  processFileUpload: PropTypes.func,
  state: PropTypes.shape({
    columnVisibility: PropTypes.object,
    setColumnVisibility: PropTypes.func,
    rowSelection: PropTypes.object,
    setRowSelection: PropTypes.func,
    columnFilters: PropTypes.array,
    setColumnFilters: PropTypes.func,
  }).isRequired,
  renderSubComponent: PropTypes.func,
  renderColumnSelector: PropTypes.func,
  enableHiding: PropTypes.bool,
  onRowClick: PropTypes.func,
  isPartialData: PropTypes.bool,
};

TableComponent.defaultProps = {
  enableHiding: true,
  isFileActions: true,
  fileActions: {
    // backwards compatibility
    enableAdd: true,
    enableUpdate: false,
    enableDelete: false,
    enableDownload: true,
    identifierColumnId: "id",
  },
  renderSubComponent: (row) => <>{JSON.stringify(row)}</>,
  renderColumnSelector: (allColumns, columnVisibility, setColumnVisibility) => (
    <ColumnSelector
      allColumns={allColumns}
      columnVisibility={columnVisibility}
      setColumnVisibility={setColumnVisibility}
    />
  ),
  onRowClick: () => {},
  isPartialData: false,
};

export default TableComponent;
